import type { FunctionComponent } from "react";
import { Suspense, useCallback, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks/sticky/app-bar-sticky-height-value";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import type { videoListQuery$key } from "scmp-app/queries/__generated__/videoListQuery.graphql";

import { Container, StyledAllVideosFilter, StyledVideoListGrid } from "./styles";

type Props = {
  className?: string;
  data: videoListQuery$key;
  enableFilter?: boolean;
};

export const VideoList: FunctionComponent<Props> = ({ className, data, enableFilter = true }) => {
  const [filterEntityUuid, setFilterEntityUuid] = useState<string>();
  const containerReference = useRef<HTMLDivElement>(null);
  const { appBarStickyHeight } = useAppBarStickyHeightValue();

  const reference = useFragment<videoListQuery$key>(
    graphql`
      fragment videoListQuery on Query
      @argumentDefinitions(
        after: { type: "String" }
        count: { type: "Int" }
        sectionIds: { type: "[String]" }
      ) {
        ...videoListGridQuery @arguments(after: $after, count: $count, sectionIds: $sectionIds)
      }
    `,
    data,
  );

  const handleFilterEntityUuidChange = useCallback(
    (entityUuid: string) => {
      setFilterEntityUuid(entityUuid);

      if (containerReference.current) {
        const containerTop = containerReference.current.getBoundingClientRect().top;
        if (containerTop < appBarStickyHeight) {
          const top = containerTop + window.scrollY - appBarStickyHeight;
          window.scrollTo({ behavior: "smooth", top });
        }
      }
    },
    [appBarStickyHeight],
  );

  return (
    <Container $enableFilter={enableFilter} className={className} ref={containerReference}>
      {enableFilter && (
        <StyledAllVideosFilter
          filterEntityUuid={filterEntityUuid}
          onFilterEntityUuidChange={handleFilterEntityUuidChange}
        />
      )}
      <Suspense fallback={<FallbackLoading />}>
        <StyledVideoListGrid filterEntityUuid={filterEntityUuid} reference={reference} />
      </Suspense>
    </Container>
  );
};
